/**
 * Reference content for the tool authoring DOCS dialog (ADR-0033 S4) — a self-contained
 * IT/EN data module: a short "how to write a tool" guide + a one-line description per
 * sandbox library. Kept OUT of the i18n locale JSON (it's reference data, not UI chrome)
 * and picked by language via `useTranslation().i18n.language` — NEVER import `@/i18n`
 * (that boots the singleton and breaks vitest; see the periodLabels gotcha).
 */
export type ToolLang = "it" | "en";
/** The tool's authoring language — the guide is TYPED on it (Python OR TypeScript, never both). */
export type ToolScript = "python" | "javascript";

/** Coarse it-vs-else pick (the app is IT-first; anything else → EN). */
export function pickToolLang(lang: string | undefined): ToolLang {
  return (lang ?? "en").toLowerCase().startsWith("it") ? "it" : "en";
}

/** Normalize an arbitrary language string to the two authoring scripts (default TS). */
export function pickToolScript(language: string | undefined): ToolScript {
  return language === "python" ? "python" : "javascript";
}

interface Bilingual {
  it: string;
  en: string;
}

export interface DocSection {
  title: string;
  body: string;
  code?: string;
}

// --- the guide (language-typed) --------------------------------------------
// A section with `only` shows for that script alone; `bodyByScript` / `codeByScript`
// override the generic body / code per script — so Python authors never see a TS
// example and vice-versa.

interface GuideDef {
  only?: ToolScript;
  title: Bilingual;
  body: Bilingual;
  bodyByScript?: Partial<Record<ToolScript, Bilingual>>;
  code?: string;
  codeByScript?: Partial<Record<ToolScript, string>>;
}

const GUIDE: GuideDef[] = [
  {
    title: { it: "Come funziona", en: "How it works" },
    body: {
      it: "Uno strumento è una FUNZIONE che riceve i suoi argomenti e RITORNA un valore. Non devi leggere/scrivere JSON: Tray se ne occupa. Lo schema degli argomenti e la descrizione vengono DEDOTTI dal codice.",
      en: "A tool is a FUNCTION that takes its arguments and RETURNS a value. You don't read/write JSON — Tray does it. The argument schema and the description are DERIVED from the code.",
    },
  },
  {
    only: "python",
    title: { it: "La funzione", en: "The function" },
    body: {
      it: "Scrivi UNA funzione decorata con @tool che ritorna un valore. I type-hint diventano lo schema (str→string, int→integer, float→number, bool→boolean, list→array, dict→object, bytes→file); un parametro senza default è obbligatorio. Il docstring diventa la descrizione.",
      en: "Write ONE @tool-decorated function that returns a value. Type hints become the schema (str→string, int→integer, float→number, bool→boolean, list→array, dict→object, bytes→file); a parameter without a default is required. The docstring becomes the description.",
    },
    code: '@tool\ndef valida_email(email: str) -> dict:\n    """Valida un indirizzo email."""\n    return {"valid": "@" in email}',
  },
  {
    only: "javascript",
    title: { it: "La funzione", en: "The function" },
    body: {
      it: "Scrivi function handler(input: Input): Output. Il tipo Input diventa lo schema, il JSDoc su handler la descrizione. Gira sull'immagine Node del sandbox; importa con require(\"nome\"). async supportato.",
      en: "Write function handler(input: Input): Output. The Input type becomes the schema, the JSDoc on handler the description. It runs on the sandbox Node image; import with require(\"name\"). async is supported.",
    },
    code: "interface Input { email: string }\ninterface Output { valid: boolean }\n/** Valida un indirizzo email. */\nfunction handler(input: Input): Output {\n  return { valid: input.email.includes(\"@\") };\n}",
  },
  {
    title: { it: "File in ingresso", en: "Input files" },
    body: {
      it: "Per ricevere un file, tipizza il parametro in modo che diventi un upload.",
      en: "To receive a file, type the parameter so it becomes an upload.",
    },
    bodyByScript: {
      python: {
        it: "Per ricevere un file, tipizza il parametro come bytes: diventa un upload. Nel codice arriva come bytes.",
        en: "To receive a file, type the parameter as bytes: it becomes an upload. In the code it arrives as bytes.",
      },
      javascript: {
        it: "Per ricevere un file, tipizza il parametro come Uint8Array / Buffer: diventa un upload. Nel codice arriva come Buffer (Node).",
        en: "To receive a file, type the parameter as Uint8Array / Buffer: it becomes an upload. In the code it arrives as a Buffer (Node).",
      },
    },
    codeByScript: {
      python: "@tool\ndef leggi(pdf: bytes) -> dict: ...",
      javascript: "interface Input { pdf: Uint8Array }\nfunction handler(input: Input) { /* input.pdf is a Buffer */ }",
    },
  },
  {
    title: { it: "File in uscita", en: "Output files" },
    body: {
      it: "Il valore di ritorno sono DATI. Se il tuo codice GENERA un file, scrivilo su disco (nella working dir) e dichiara il suo nome nel campo \"File di output\": Tray lo raccoglie e lo rende scaricabile (test-run) o lo passa al modello come riferimento file.",
      en: "The return value is DATA. If your code GENERATES a file, write it to disk (in the working dir) and declare its name in the \"Output files\" field: Tray harvests it and makes it downloadable (test-run) or hands it to the model as a file reference.",
    },
    codeByScript: {
      python: 'with open("slides.pptx", "wb") as f:\n    f.write(data)',
      javascript: 'require("fs").writeFileSync("slides.pptx", buffer);',
    },
  },
  {
    title: { it: "Librerie", en: "Libraries" },
    body: {
      it: "Le librerie qui sotto sono GIÀ installate nel sandbox: importale direttamente, non serve dichiararle. Non c'è accesso a rete né a credenziali/DB — uno strumento vede solo i suoi argomenti e file.",
      en: "The libraries below are ALREADY installed in the sandbox: import them directly, no need to declare them. There is no network / no credentials / no DB — a tool only sees its own arguments and files.",
    },
  },
];

/** The guide for one language + one authoring script — no cross-language examples. */
export function toolGuideSections(lang: ToolLang, script: ToolScript): DocSection[] {
  return GUIDE.filter((s) => !s.only || s.only === script).map((s) => ({
    title: s.title[lang],
    body: (s.bodyByScript?.[script] ?? s.body)[lang],
    code: s.codeByScript?.[script] ?? s.code,
  }));
}

// --- static UI labels (kept next to the content, same language pick) --------

export const DOCS_LABELS: Record<ToolLang, {
  button: string;
  title: string;
  guide: string;
  libraries: string;
  search: string;
  close: string;
  noMatch: string;
  useCases: string;
}> = {
  it: {
    button: "Documentazione",
    title: "Come scrivere uno strumento",
    guide: "Guida",
    libraries: "Librerie disponibili",
    search: "Cerca libreria…",
    close: "Chiudi",
    noMatch: "Nessuna corrispondenza.",
    useCases: "Casi d’uso",
  },
  en: {
    button: "Documentation",
    title: "How to write a tool",
    guide: "Guide",
    libraries: "Available libraries",
    search: "Search a library…",
    close: "Close",
    noMatch: "No match.",
    useCases: "Use cases",
  },
};

// --- one-line description per sandbox library (the fat-image allow-list) -----

const LIB_DESC: Record<string, Bilingual> = {
  // --- Python ---
  pymupdf: { it: "PDF: lettura/rendering veloce (fitz)", en: "PDF: fast read/render (fitz)" },
  pypdf: { it: "PDF: unione/split/estrazione pagine", en: "PDF: merge/split/extract pages" },
  reportlab: { it: "PDF: generazione programmatica", en: "PDF: programmatic generation" },
  pdfplumber: { it: "PDF: estrazione testo e tabelle", en: "PDF: extract text and tables" },
  pikepdf: { it: "PDF: manipolazione low-level (qpdf)", en: "PDF: low-level manipulation (qpdf)" },
  borb: { it: "PDF: creazione/lettura ricca", en: "PDF: rich create/read" },
  weasyprint: { it: "HTML/CSS → PDF", en: "HTML/CSS → PDF" },
  pyhanko: { it: "PDF: firma digitale (eIDAS)", en: "PDF: digital signing (eIDAS)" },
  "python-docx": { it: "Word (.docx): lettura/scrittura", en: "Word (.docx): read/write" },
  "python-pptx": { it: "PowerPoint (.pptx): lettura/scrittura", en: "PowerPoint (.pptx): read/write" },
  openpyxl: { it: "Excel (.xlsx): lettura/scrittura", en: "Excel (.xlsx): read/write" },
  xlsxwriter: { it: "Excel (.xlsx): scrittura + grafici", en: "Excel (.xlsx): write + charts" },
  xlrd: { it: "Excel (.xls) legacy: lettura", en: "Excel (.xls) legacy: read" },
  docxtpl: { it: "Word da template Jinja", en: "Word from Jinja templates" },
  odfpy: { it: "OpenDocument (.odt/.ods)", en: "OpenDocument (.odt/.ods)" },
  ezodf: { it: "OpenDocument: API più semplice", en: "OpenDocument: simpler API" },
  markdown: { it: "Markdown → HTML", en: "Markdown → HTML" },
  markdownify: { it: "HTML → Markdown", en: "HTML → Markdown" },
  jinja2: { it: "Templating di testo", en: "Text templating" },
  numpy: { it: "Array e calcolo numerico", en: "Arrays and numeric computing" },
  pandas: { it: "DataFrame: analisi dati", en: "DataFrames: data analysis" },
  polars: { it: "DataFrame veloci (Rust)", en: "Fast DataFrames (Rust)" },
  pyarrow: { it: "Arrow/Parquet columnar", en: "Arrow/Parquet columnar" },
  tabulate: { it: "Tabelle testuali formattate", en: "Formatted text tables" },
  pyyaml: { it: "YAML: parse/dump", en: "YAML: parse/dump" },
  tomli: { it: "TOML: lettura", en: "TOML: read" },
  "tomli-w": { it: "TOML: scrittura", en: "TOML: write" },
  jsonschema: { it: "Validazione JSON Schema", en: "JSON Schema validation" },
  xmltodict: { it: "XML ↔ dict", en: "XML ↔ dict" },
  lxml: { it: "XML/HTML: parsing veloce", en: "XML/HTML: fast parsing" },
  beautifulsoup4: { it: "HTML: parsing tollerante", en: "HTML: fault-tolerant parsing" },
  matplotlib: { it: "Grafici statici", en: "Static charts" },
  seaborn: { it: "Grafici statistici (su matplotlib)", en: "Statistical charts (on matplotlib)" },
  plotly: { it: "Grafici (esporta PNG con kaleido)", en: "Charts (PNG export via kaleido)" },
  kaleido: { it: "Export immagini per Plotly", en: "Image export for Plotly" },
  Pillow: { it: "Immagini: manipolazione", en: "Images: manipulation" },
  qrcode: { it: "Generazione QR code", en: "QR code generation" },
  "python-barcode": { it: "Generazione codici a barre", en: "Barcode generation" },
  scipy: { it: "Calcolo scientifico", en: "Scientific computing" },
  "scikit-learn": { it: "Machine learning", en: "Machine learning" },
  statsmodels: { it: "Statistica ed econometria", en: "Statistics and econometrics" },
  cryptography: { it: "Crittografia (hash/cifratura/firma)", en: "Cryptography (hash/encrypt/sign)" },
  bcrypt: { it: "Hashing password", en: "Password hashing" },
  pydantic: { it: "Validazione/modelli dati", en: "Data validation/models" },
  rich: { it: "Output testuale ricco", en: "Rich text output" },
  "python-dateutil": { it: "Date: parsing/aritmetica", en: "Dates: parsing/arithmetic" },
  pytz: { it: "Fusi orari", en: "Time zones" },
  regex: { it: "Regex avanzate", en: "Advanced regex" },
  chardet: { it: "Rilevamento encoding", en: "Encoding detection" },
  "python-magic": { it: "Rilevamento tipo file", en: "File type detection" },
  phonenumbers: { it: "Validazione numeri di telefono", en: "Phone number validation" },
  "email-validator": { it: "Validazione email", en: "Email validation" },
  click: { it: "CLI/parsing argomenti", en: "CLI/argument parsing" },
  // --- JavaScript / TypeScript ---
  "pdf-lib": { it: "PDF: crea/modifica", en: "PDF: create/modify" },
  pdfkit: { it: "PDF: generazione via stream", en: "PDF: streaming generation" },
  "pdfjs-dist": { it: "PDF: rendering/parsing (Mozilla)", en: "PDF: render/parse (Mozilla)" },
  "pdf-parse": { it: "PDF: estrazione testo", en: "PDF: text extraction" },
  xlsx: { it: "Excel/CSV: lettura/scrittura (SheetJS)", en: "Excel/CSV: read/write (SheetJS)" },
  exceljs: { it: "Excel: scrittura ricca + stili", en: "Excel: rich write + styles" },
  "csv-parse": { it: "CSV: parsing", en: "CSV: parsing" },
  "csv-stringify": { it: "CSV: scrittura", en: "CSV: writing" },
  papaparse: { it: "CSV: parser veloce", en: "CSV: fast parser" },
  docx: { it: "Word (.docx): generazione", en: "Word (.docx): generation" },
  mammoth: { it: "Word (.docx) → HTML", en: "Word (.docx) → HTML" },
  marked: { it: "Markdown → HTML", en: "Markdown → HTML" },
  turndown: { it: "HTML → Markdown", en: "HTML → Markdown" },
  cheerio: { it: "HTML: parsing stile jQuery", en: "HTML: jQuery-style parsing" },
  xml2js: { it: "XML ↔ oggetto JS", en: "XML ↔ JS object" },
  pptxgenjs: { it: "PowerPoint (.pptx): generazione", en: "PowerPoint (.pptx): generation" },
  "office-text-extractor": { it: "Estrazione testo da Office", en: "Text extraction from Office" },
  "chart.js": { it: "Grafici (con canvas per PNG)", en: "Charts (with canvas for PNG)" },
  "chartjs-node-canvas": { it: "Chart.js → PNG server-side", en: "Chart.js → PNG server-side" },
  canvas: { it: "Canvas 2D (immagini)", en: "2D canvas (images)" },
  "d3-array": { it: "D3: utilità su array/statistiche", en: "D3: array/stats utilities" },
  "d3-scale": { it: "D3: scale per grafici", en: "D3: chart scales" },
  "d3-shape": { it: "D3: generatori di forme", en: "D3: shape generators" },
  sharp: { it: "Immagini: resize/convert ad alte performance", en: "Images: high-perf resize/convert" },
  "bwip-js": { it: "Codici a barre (molti formati)", en: "Barcodes (many formats)" },
  lodash: { it: "Utility per array/oggetti", en: "Array/object utilities" },
  dayjs: { it: "Date: parse/format leggero", en: "Dates: lightweight parse/format" },
  "js-yaml": { it: "YAML: parse/dump", en: "YAML: parse/dump" },
  mathjs: { it: "Matematica estesa", en: "Extended math" },
  "simple-statistics": { it: "Statistica di base", en: "Basic statistics" },
  uuid: { it: "Generazione UUID", en: "UUID generation" },
  ajv: { it: "Validazione JSON Schema", en: "JSON Schema validation" },
  joi: { it: "Validazione schemi oggetti", en: "Object schema validation" },
  bcryptjs: { it: "Hashing password", en: "Password hashing" },
  jsonwebtoken: { it: "JWT: firma/verifica", en: "JWT: sign/verify" },
  "node-forge": { it: "Crittografia (TLS/PKI)", en: "Cryptography (TLS/PKI)" },
  "iconv-lite": { it: "Conversione encoding", en: "Encoding conversion" },
  validator: { it: "Validatori (email/URL/…)", en: "Validators (email/URL/…)" },
  "libphonenumber-js": { it: "Numeri di telefono", en: "Phone numbers" },
};

/** A short description for a library, or "" when we don't curate one (never a lie). */
export function libraryDescription(name: string, lang: ToolLang): string {
  return LIB_DESC[name]?.[lang] ?? "";
}
